import { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react';
import AppHeader from '../components/AppHeader';
import MessageDialog from '../components/MessageDialog';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { fetchSession, hasPermission } from '../lib/session';

function defaultRoleName(options: string[]): string {
  if (options.includes('user')) return 'user';
  return options[0] ?? '';
}

type User = {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  isActive: boolean;
  lastLoginAt: string | null;
  inviteAcceptedAt: string | null;
  createdAt: string;
  roles: string[];
};

type InviteLink = { url: string; expiresAt: string };

function isPendingUser(user: User): boolean {
  return !user.isActive && !user.inviteAcceptedAt;
}

function statusLabel(user: User): string {
  if (user.isActive) return 'Active';
  if (!user.inviteAcceptedAt) return 'Pending';
  return 'Inactive';
}

function isUser(value: unknown): value is User {
  if (!value || typeof value !== 'object') return false;
  const row = value as Record<string, unknown>;
  return typeof row.id === 'string'
    && typeof row.email === 'string'
    && typeof row.isActive === 'boolean'
    && (row.lastLoginAt === null || row.lastLoginAt !== undefined)
    && (row.inviteAcceptedAt === null || row.inviteAcceptedAt !== undefined)
    && row.createdAt !== undefined
    && Array.isArray(row.roles)
    && row.roles.every((item) => typeof item === 'string');
}

function isInviteResponse(value: unknown): value is { inviteUrl: string; expiresAt: string } {
  return !!value
    && typeof value === 'object'
    && typeof (value as { inviteUrl?: unknown }).inviteUrl === 'string'
    && (value as { inviteUrl: string }).inviteUrl.length > 0
    && (value as { expiresAt?: unknown }).expiresAt !== undefined;
}

function inviteUrlForBrowser(serverUrl: string): string {
  try {
    const parsed = new URL(serverUrl, window.location.origin);
    return `${window.location.origin}${parsed.pathname}${parsed.search}`;
  } catch {
    return serverUrl;
  }
}

export default function AdminUsers() {
  const [items, setItems] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', roles: ['user'] });
  const [formError, setFormError] = useState<string | null>(null);
  const [invite, setInvite] = useState<InviteLink | null>(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState<{ title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    const res = await fetch(`/api/admin/users?q=${encodeURIComponent(q)}&page=${page}&pageSize=${pageSize}`);
    if (res.status === 403) {
      setItems([]);
      setTotal(0);
      setRoleOptions([]);
      return;
    }
    const data: unknown = await res.json();
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid users response');
    }
    const payload = data as { items?: unknown; total?: unknown; roleOptions?: unknown };
    setItems(Array.isArray(payload.items) ? payload.items.filter(isUser) : []);
    setTotal(typeof payload.total === 'number' ? payload.total : 0);
    setRoleOptions(Array.isArray(payload.roleOptions) ? payload.roleOptions.filter((item): item is string => typeof item === 'string') : []);
  }, [q, page, pageSize]);

  useEffect(() => {
    (async () => {
      const session = await fetchSession();
      setCanWrite(hasPermission(session, 'users.write'));
    })();
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    setForm((current) => {
      const currentRole = current.roles[0];
      if (currentRole && roleOptions.includes(currentRole)) return current;
      const nextRole = defaultRoleName(roleOptions);
      return { ...current, roles: nextRole ? [nextRole] : [] };
    });
  }, [roleOptions]);

  function resetCreateForm() {
    const nextRole = defaultRoleName(roleOptions);
    setForm({ email: '', firstName: '', lastName: '', roles: nextRole ? [nextRole] : [] });
    setFormError(null);
  }

  async function copyInvite(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  async function createUser() {
    const role = form.roles[0];
    if (!role || !roleOptions.includes(role)) return;
    setFormError(null);
    const res = await fetch('/api/admin/users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...form, email: form.email.trim().toLowerCase() }),
    });
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok || !isInviteResponse(data)) {
      const message = data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : 'Invite failed';
      setFormError(message);
      return;
    }
    setOpen(false);
    resetCreateForm();
    setInvite({ url: inviteUrlForBrowser(data.inviteUrl), expiresAt: String(data.expiresAt) });
    setCopied(false);
    load();
  }

  async function createInviteLink(user: User) {
    const res = await fetch(`/api/admin/users/${user.id}/invite`, { method: 'POST' });
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok || !isInviteResponse(data)) {
      const message = data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : 'Could not create invite link';
      setNotice({ title: 'Invite failed', message });
      return;
    }
    setInvite({ url: inviteUrlForBrowser(data.inviteUrl), expiresAt: String(data.expiresAt) });
    setCopied(false);
  }

  async function toggleActive(user: User) {
    await fetch(`/api/admin/users/${user.id}/status`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ isActive: !user.isActive }) });
    load();
  }

  async function removeUser(user: User) {
    await fetch(`/api/admin/users/${user.id}`, { method: 'DELETE' });
    load();
  }

  async function resetPassword(user: User) {
    const res = await fetch(`/api/admin/users/${user.id}/reset`, { method: 'POST' });
    if (!res.ok) {
      const data: unknown = await res.json().catch(() => null);
      const message = data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string'
        ? (data as { error: string }).error
        : 'Could not reset password';
      setNotice({ title: 'Reset failed', message });
      return;
    }
    setNotice({
      title: 'Password reset',
      message: 'If SMTP is configured, a reset email was sent; otherwise the link was logged on the server.',
    });
  }

  return (
    <div className="min-h-screen">
      <AppHeader />
      <MessageDialog notice={notice} onClose={() => setNotice(null)} />
      <div className="page-frame">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold">Users</h1>
          {canWrite && (
            <Button type="button" onClick={() => { resetCreateForm(); setOpen(true); }}>Invite User</Button>
          )}
          <Dialog open={invite !== null} onOpenChange={(next) => { if (!next) { setInvite(null); setCopied(false); } }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite link</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3">
                <p className="text-sm text-muted-foreground">
                  Send this link offline. It expires in 3 days and can only be used once.
                </p>
                <Input id="invite-url" readOnly value={invite?.url ?? ''} onFocus={(event) => event.currentTarget.select()} />
                <Button
                  type="button"
                  onClick={() => { if (invite) void copyInvite(invite.url); }}
                >
                  {copied ? 'Copied' : 'Copy link'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite User</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="firstName">First name</Label>
                    <Input id="firstName" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last name</Label>
                    <Input id="lastName" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={form.roles[0] && roleOptions.includes(form.roles[0]) ? form.roles[0] : undefined}
                    onValueChange={(value) => setForm((current) => ({ ...current, roles: [value] }))}
                    disabled={roleOptions.length === 0}
                  >
                    <SelectTrigger id="role" aria-label="Role">
                      <SelectValue placeholder="Select a role" />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map((role) => (
                        <SelectItem key={role} value={role}>{role}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formError && <p className="text-sm text-destructive">{formError}</p>}
                <Button onClick={createUser} disabled={!form.email || roleOptions.length === 0 || !form.roles[0] || !roleOptions.includes(form.roles[0])}>Create invite</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="mb-4">
          <Input placeholder="Search email..." value={q} onChange={e => { setPage(1); setQ(e.target.value); }} />
        </div>
        <div className="overflow-x-auto border rounded-md">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="text-left p-2 font-semibold">Email</th>
                <th className="text-left p-2 font-semibold">Name</th>
                <th className="text-left p-2 font-semibold">Roles</th>
                <th className="text-left p-2 font-semibold">Status</th>
                <th className="text-left p-2 font-semibold">Created</th>
                <th className="text-left p-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-t">
                  <td className="p-2">{u.email}</td>
                  <td className="p-2">{`${u.firstName || ''} ${u.lastName || ''}`.trim()}</td>
                  <td className="p-2">{u.roles.length ? u.roles.join(', ') : '—'}</td>
                  <td className="p-2">{statusLabel(u)}</td>
                  <td className="p-2">{new Date(u.createdAt).toLocaleString()}</td>
                  <td className="p-2">
                    {canWrite && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          aria-label={`Actions for ${u.email}`}
                        >
                          <MoreHorizontal />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {isPendingUser(u) ? (
                          <DropdownMenuItem onClick={() => createInviteLink(u)}>
                            Copy invite link
                          </DropdownMenuItem>
                        ) : (
                          <>
                            <DropdownMenuItem onClick={() => toggleActive(u)}>
                              {u.isActive ? 'Deactivate' : 'Activate'}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => resetPassword(u)}>
                              Reset password
                            </DropdownMenuItem>
                          </>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:bg-destructive/15 focus:text-destructive"
                          onClick={() => removeUser(u)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between mt-3">
          <div>Page {page} of {Math.max(1, Math.ceil(total / pageSize))} • {total} total</div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              aria-label="Previous page"
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              disabled={page >= Math.max(1, Math.ceil(total / pageSize))}
              onClick={() => setPage(page + 1)}
              aria-label="Next page"
            >
              <ChevronRight />
            </Button>
            <select
              value={pageSize}
              onChange={e => setPageSize(parseInt(e.target.value, 10))}
              aria-label="Rows per page"
              className="h-8 rounded-md border border-input bg-background px-2 text-sm"
            >
              {[10, 20, 50].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

